import type { OnboardingConfig } from "../../lib/onboarding/flowConfig.fallback";
import { resolveStepCatalogTypes } from "../../lib/onboarding/journeyEnumCatalog";
import { deriveRoleOptions } from "./deriveOptions";

// Enum-normalized answer snapshot the guards read (subset of OnboardingAnswers).
export interface FlowAnswers {
	gender?: string;
	fertilityStatus?: string;
	fertilityRole?: string;
	fertilityJourneyTypes?: string[];
}

function statusRule(config: OnboardingConfig, a: FlowAnswers) {
	return a.fertilityStatus
		? config.flow[a.fertilityStatus as keyof typeof config.flow]
		: undefined;
}

// §6.3 — skip role when the status has no role options.
export function shouldSkipRole(
	config: OnboardingConfig,
	a: FlowAnswers,
): boolean {
	return (
		deriveRoleOptions(config, a.fertilityStatus as never, a.gender as never)
			.length === 0
	);
}

// §6.4 — skip journey when status hides it OR role is surrogate.
export function shouldSkipJourney(
	config: OnboardingConfig,
	a: FlowAnswers,
): boolean {
	const rule = statusRule(config, a);
	if (!rule?.s7.show) return true;
	if (a.fertilityRole === "surrogate") return true;
	return false;
}

// §6.5 — skip steps when the status hides them, or there is no step catalog to
// render (dont_know/other — an empty steps screen would trap the user on a
// disabled Confirm). Donation journeys DO show steps (recipient catalogs);
// product decision 2026-06-16.
export function shouldSkipSteps(
	config: OnboardingConfig,
	a: FlowAnswers,
): boolean {
	if (!statusRule(config, a)?.s8.show) return true;
	const journeys = a.fertilityJourneyTypes ?? [];
	return (
		resolveStepCatalogTypes(a.fertilityStatus, a.fertilityRole, journeys)
			.length === 0
	);
}

// Cycle screen gating (FigJam Screen 9, product-confirmed 2026-06-17): the
// journeys whose orange line routes to the cycle "skip this screen" marker are
// PER-STATUS, not global. TTC/preserving skip only egg_donation; surrogacy also
// skips surrogacy + egg_freezing (the intended parent's own cycle isn't tracked
// on those paths). Statuses absent here have no journey-based cycle screen.
const CYCLE_EXCLUDED_JOURNEYS: Record<string, string[]> = {
	trying_to_conceive: ["egg_donation"],
	preserving_fertility: ["egg_donation"],
	surrogacy: ["egg_donation", "surrogacy", "egg_freezing"],
};

export function shouldSkipCycle(
	_config: OnboardingConfig,
	a: FlowAnswers,
): boolean {
	// Surrogate carries the pregnancy and never sees the journey screen, so the
	// cycle is always relevant — evaluate this BEFORE the journey-exclusion check
	// (a stale journey value left over from a pre-Back role change must not skip).
	if (a.fertilityRole === "surrogate") return false;
	if (a.gender !== "female") return true;
	const excluded = a.fertilityStatus
		? (CYCLE_EXCLUDED_JOURNEYS[a.fertilityStatus] ?? [])
		: [];
	const excludedJourney = (a.fertilityJourneyTypes ?? []).some((j) =>
		excluded.includes(j),
	);
	if (excludedJourney) return true;
	if (
		a.fertilityStatus === "surrogacy" &&
		a.fertilityRole === "intended_parent"
	) {
		return false;
	}
	const cycleStatuses = new Set(["trying_to_conceive", "preserving_fertility"]);
	const show =
		!!a.fertilityStatus &&
		cycleStatuses.has(a.fertilityStatus) &&
		a.fertilityRole === "primary_patient";
	return !show;
}

// §3 — egg_sperm_donor short-circuits the whole branch (role→cycle skipped).
export function isDonor(a: FlowAnswers): boolean {
	return a.fertilityStatus === "egg_sperm_donor";
}
